/**
 * Модули воркспейса — необязательные разделы продукта, которые администратор
 * включает/выключает в настройках.
 *
 * Хранятся в `workspace.metadata.modules` (бэкенд принимает `metadata` в PATCH
 * /workspaces/{sid} как единое поле). Отсутствие флага = модуль выключен:
 * необязательные разделы включает администратор воркспейса осознанно.
 */
import type {Workspace} from '../types';

/** Набор переключаемых модулей воркспейса. */
export interface WorkspaceModules {
  /** Раздел «Встречи» + фильтр по типу коммуникации на дашборде. */
  meetings: boolean;
  /** Сохранённые аналитические инсайты вместо анонса на дашборде. */
  analyticsInsights: boolean;
}

/** Значения по умолчанию: необязательные модули выключены. */
export const DEFAULT_MODULES: WorkspaceModules = {
  meetings: false,
  analyticsInsights: false,
};

/** Ключ в `workspace.metadata`, под которым лежат флаги модулей. */
const MODULES_KEY = 'modules';

/** Флаги модулей воркспейса (с дефолтами для отсутствующих ключей). */
export const readModules = (
  workspace: Workspace | null | undefined,
): WorkspaceModules => {
  const raw = workspace?.metadata?.[MODULES_KEY];
  if (!raw || typeof raw !== 'object') return DEFAULT_MODULES;
  const modules = raw as Partial<Record<keyof WorkspaceModules, unknown>>;
  return {
    meetings:
      typeof modules.meetings === 'boolean'
        ? modules.meetings
        : DEFAULT_MODULES.meetings,
    analyticsInsights: modules.analyticsInsights === true,
  };
};

/**
 * Метаданные воркспейса с обновлёнными флагами модулей.
 *
 * PATCH заменяет `metadata` целиком, поэтому остальные ключи переносим как есть
 * (read-mutate-write) — иначе затрём чужие служебные данные.
 */
export const withModules = (
  workspace: Workspace,
  modules: WorkspaceModules,
): Record<string, unknown> => {
  const currentModules = workspace.metadata?.[MODULES_KEY];
  return {
    ...(workspace.metadata ?? {}),
    [MODULES_KEY]: {
      ...(currentModules && typeof currentModules === 'object'
        ? currentModules
        : {}),
      ...modules,
    },
  };
};
